import ApiServer from "../../ApiServer.ts";
import * as FwDatas from "../../FwDatas.ts";
import * as i18n from "../../i18n.ts";
import * as amplify from "../../util/amplify.ts";
import * as Storage from "../../lib/Storage.ts";
import IHasRecurrencyHelper, {type IHasRecurrencyOccurrence} from "../../lib/IHasRecurrencyHelper.ts"
import * as DateUtils from "../../lib/DateUtils.ts"
import {SyncCache} from "../../lib/SyncCache.ts";
import { BudgetBean, BudgetCategoryBean, BudgetTransactionBean, PaymentMethodBean } from "../fizapilib.auto.ts";

export {getCache,setCurrentBudget,getCurrentBudget,buildTransactionList,buildSummary}

/** an occurrence is prototype-linked to its transaction, so it carries every transaction field too */
export type BudgetTransactionOccurrence = BudgetTransactionBean & IHasRecurrencyOccurrence;

/** a bean the user can hide, only the ones of their language are proposed */
interface LocalizedBean {
  locale?: string;
  hidden?: boolean;
  default?: boolean;
}

/** the category and payment method caches also expose the language filtered views */
interface FilteredCache<T> extends SyncCache<T> {
  filtered(): T[];
  hidden(): T[];
}

export interface BudgetSummary {
  balance: number;
  balanceString: string | null;
  revenue: number;
  revenueString: string | null;
  expense: number;
  expenseString: string | null;
  topCategoryTitle: string | null;
  topCategory: number;
  topCategoryString: string | null;
  topPayerTitle: string | null;
  topPayer: number;
  topPayerString: string | null;
}

// Augmentation of the generated beans with the properties and methods added dynamically below
declare module "../fizapilib.auto.ts" {
  interface BudgetBean {
    formatter?: Intl.NumberFormat;
    formatterRound?: Intl.NumberFormat;
    isRecurrent?: boolean;
    sharingType?: 'all' | 'custom' | 'none';
    /** what the currency format puts before and after the amount */
    beforeAmount?: string;
    afterAmount?: string;
    isCategoryLimitValid(): boolean;
    formatAmount(amount: number): string | null;
    formatAmountRound(amount: number): string | null;
    getTransactionList(range?: DateUtils.DateRange, filter?: any): BudgetTransactionBean[];
    getRange(date: Date): DateUtils.DateRange;
  }
  interface BudgetTransactionBean {
    isExpense?: boolean;
    /** planned for a later day */
    isUpcoming?: boolean;
    isPaid(): boolean;
    budgetCategory(): BudgetCategoryBean;
    budget(): BudgetBean;
    paymentMethod(): PaymentMethodBean;
    relatedAccount(): any;
    amountString(): string;
  }
  interface BudgetCategoryBean {
    compareTo(another: BudgetCategoryBean): number;
  }
  interface PaymentMethodBean {
    compareTo(another: PaymentMethodBean): number;
  }
}

function compareName(a: {name?: string}, b: {name?: string}): number {
  //TODO understand why using a.name.localCompare(b.name) causes : TypeError: a.name.localCompare is not a function
   return String.prototype.localeCompare.apply(a.name,[b.name])
}
function compareSortingIndex(a: {sortingIndex?: number}, b: {sortingIndex?: number}): number {
   return a.sortingIndex-b.sortingIndex
}

const
  budgetCache = new SyncCache<BudgetBean>(ApiServer.budgetsync,compareName),
  budgetCategoryCache = new SyncCache<BudgetCategoryBean>(ApiServer.budgetcategorysync,compareSortingIndex) as FilteredCache<BudgetCategoryBean>,
  budgetPaymentMethodCache = new SyncCache<PaymentMethodBean>(ApiServer.budgetpaymentmethodsync,compareSortingIndex) as FilteredCache<PaymentMethodBean>,
  budgetTransactionCache = new SyncCache<BudgetTransactionBean>(ApiServer.budgettransactionsync);

function getCache(): any {
  return {budgetCache,budgetCategoryCache,budgetPaymentMethodCache,budgetTransactionCache}
}

//filter data of the locale of the guy or 'en' if not in our bases
function langfilter<T extends LocalizedBean>(datas: T[]): T[] {
  var langInData = new Set();
  datas.forEach(cat=>langInData.add(cat.locale));
  var lang = i18n.getShortLanguage();
  if(!langInData.has(lang)){
    lang = 'en'
  }
  return datas.filter(cat=>cat.locale===undefined ||cat.locale===lang);
}

budgetCategoryCache.filtered = function(): BudgetCategoryBean[] {
  return langfilter(budgetCategoryCache.datas).filter(cat=>(!cat.hidden || cat.default))
}
budgetCategoryCache.hidden = function(): BudgetCategoryBean[] {
  return langfilter(budgetCategoryCache.datas).filter(cat=>(cat.hidden && !cat.default))
}
budgetPaymentMethodCache.filtered = function(): PaymentMethodBean[] {
  return langfilter(budgetPaymentMethodCache.datas).filter(cat=>(!cat.hidden || cat.default))
}
budgetPaymentMethodCache.hidden = function(): PaymentMethodBean[] {
  return langfilter(budgetPaymentMethodCache.datas).filter(cat=>(cat.hidden && !cat.default))
}
amplify.subscribe("budgetNeedRefresh",function(){
  refreshAll().then(function(){
    amplify.publish("budgetRefreshed")
  })
});
function refreshAll(): Promise<void> {
  return new Promise((accept,reject)=>{
    budgetCache.refresh()
    budgetCategoryCache.refresh()
    budgetPaymentMethodCache.refresh()
    budgetTransactionCache.refresh()
    ApiServer.aftergroup(accept)
    //FIXME reject should be somewhere
  })
}

Object.assign(BudgetBean.prototype,{
  onDecode(this: BudgetBean): void {
    this.formatter = new Intl.NumberFormat(i18n.getSelectedLanguage(), {
      style: 'currency',
      currency: this.currencyCode
    });
    this.formatterRound = new Intl.NumberFormat(i18n.getSelectedLanguage(), {
      style: 'currency',
      currency: this.currencyCode,
      maximumFractionDigits:0,
      minimumFractionDigits:0
    });
    this.isRecurrent = this.recurrencyDescriptor.recurrency!=='NONE';
    this.sharingType = this.sharedToAll?'all':(this.sharedMemberIds.length>1?'custom':'none')
    this.rights.canShare = this.accountId === FwDatas.getAccountId()

    var
      template = this.formatterRound.format(0).split(/0/g);
    this.beforeAmount = template[0];
    this.afterAmount = template[1];

  },
  isCategoryLimitValid(this: BudgetBean): boolean {
    //budgetCategoryLimits is decoded by decodeMap() which builds a plain object, not a Map
    const limitByCategoryId = this.budgetCategoryLimits as unknown as Record<string, number>;
    return this.budgetLimit>= Object.values(limitByCategoryId).reduce((prev,current)=>prev+current,0)
  },
  formatAmount(this: BudgetBean, amount: number): string | null {
    if(isNaN(amount)) return null;
    return this.formatter.format(amount)
  },
  formatAmountRound(this: BudgetBean, amount: number): string | null {
    if(isNaN(amount)) return null;
    return this.formatterRound.format(amount)
  },
  getTransactionList(this: BudgetBean, range: DateUtils.DateRange = IHasRecurrencyHelper.getRange(this.recurrencyDescriptor.recurrency,new Date()), filter: any = {}): BudgetTransactionBean[] {
    var budgetTransactions = budgetTransactionCache.datas.filter(t=>t.budgetId===this.metaId);
    return buildTransactionList(range,budgetTransactions)
  },
  getRange(this: BudgetBean, date: Date): DateUtils.DateRange {
    if(this.recurrencyDescriptor.recurrency==="NONE"){
      return  IHasRecurrencyHelper.getRange(this.recurrencyDescriptor.recurrency,date)
    }
    if(this.recurrencyDescriptor.recurrency==="BIWEEKLY"){
      return DateUtils.getWeekRange(date,this.recurrencyStartDay,2);
    }

    const START = DateUtils.clearTime(date)
    START.setFullYear(START.getFullYear()-2)
    //TODO put this in IHasRecurrencyHelper.getRange(
    var
      allDates = IHasRecurrencyHelper.listInterval({startDate:START,allDay:true,recurrencyDescriptor:this.recurrencyDescriptor},{start:START,end:date},1).map(interval=>interval.start),
      start = allDates[allDates.length-2],
      end = allDates[allDates.length-1],
      prev = new Date(start.getTime()-1000),
      next = new Date(end.getTime()),
      rangeTextEnd = new Date(end.getTime());
    next.setDate(next.getDate()+1)
    rangeTextEnd.setDate(rangeTextEnd.getDate()-1)

    var name = DateUtils.dayRangeString(start,rangeTextEnd);
    return new DateUtils.DateRange({
      start:start,
      end:end,
      prev:prev,
      next:next,
      recurrency:this.recurrencyDescriptor.recurrency,
      name:name
    })
  }
})

Object.assign(BudgetTransactionBean.prototype,{
  onDecode(this: BudgetTransactionBean): void {
    FwDatas.cache(this);
    this.isExpense = (this.budgetTransactionType==="EXPENSE");
    var now = new Date();
    this.isUpcoming = this.startDate>now && !DateUtils.isSameDay(now,this.startDate);
  },
  isPaid(this: BudgetTransactionOccurrence): boolean {
    return this.paidOccurence.indexOf(this.occurenceIndex||0)!==-1
  },
  budgetCategory(this: BudgetTransactionBean): BudgetCategoryBean {
    return FwDatas.get(this.budgetCategoryId);
  },
  budget(this: BudgetTransactionBean): BudgetBean {
    return FwDatas.get(this.budgetId);
  },
  paymentMethod(this: BudgetTransactionBean): PaymentMethodBean {
    return FwDatas.get(this.paymentMethodId);
  },
  relatedAccount(this: BudgetTransactionBean): any {
    return FwDatas.getProfile(this.relatedAccountId);
  },
  amountString(this: BudgetTransactionBean): string {
    var str = this.budget().formatAmount(-this.amount)
    if(this.budgetTransactionType==="EXPENSE"){
      return str;
    }else{
      return str.replace('-','+')
    }
  }
});


Object.assign(BudgetCategoryBean.prototype,{
  onDecode(this: BudgetCategoryBean): void {
    if(this.emoji===undefined){
      this.emoji='💰'
    }
    FwDatas.cache(this)
  },
  compareTo(this: BudgetCategoryBean, another: BudgetCategoryBean): number {
    return this.name.localeCompare(another.name)
  }
});
Object.assign(PaymentMethodBean.prototype,{
  onDecode(this: PaymentMethodBean): void {
    if(this.emoji===undefined){
      this.emoji='💰'
    }
    FwDatas.cache(this)
  },
  compareTo(this: PaymentMethodBean, another: PaymentMethodBean): number {
    return this.name.localeCompare(another.name)
  }
});

function setCurrentBudget(budgetId: string): void {
  Storage.localSet('budget.current',budgetId)
}
function getCurrentBudget(): BudgetBean {
  var
    metaId = Storage.localGet('budget.current'),
    budget = FwDatas.get(metaId);

  if(!budget){
    budget  = budgetCache.datas.filter(t=>t.metaId===Storage.localGet('budget.current'))[0]||budgetCache.datas[0];
  }
  return budget;
}

function buildTransactionList(range: DateUtils.DateRange, transactionList: BudgetTransactionBean[]): BudgetTransactionBean[] {
  var result: BudgetTransactionBean[] = [];
  transactionList.forEach(transaction=>{
    var
      rrule = transaction.recurrencyDescriptor.rrule;
    if(rrule){
      (IHasRecurrencyHelper.listInterval(transaction,range) as BudgetTransactionOccurrence[]).forEach(t=>{
        FwDatas.cache(t)
        t.startDate = t.start;
        result.push(t);
      })

    }else if(transaction.startDate>=range.start && transaction.startDate<range.end){
      result.push(transaction);
    }

  })
  return result;
}
function buildSummary(budget: BudgetBean, transactionList: BudgetTransactionBean[]): BudgetSummary {
  var
    balance = 0,revenue =0,expense =0,expenseByCat: Record<string, number> = {},expenseByUser: Record<string, number> = {};

  if(budget.paidUsage ==='PAID'){
    transactionList = transactionList.filter(transaction=>transaction.isPaid())
  }
  if(transactionList.length===0){
    return {
      balance : balance,
      balanceString : budget.formatAmount(balance),
      revenue : revenue,
      revenueString : budget.formatAmount(revenue),
      expense : expense,
      expenseString : budget.formatAmount(expense),

      topCategoryTitle : null,
      topCategory : 0,
      topCategoryString : budget.formatAmount(0),
      topPayerTitle : null,
      topPayer : 0,
      topPayerString : budget.formatAmount(0)
    }
  }

  transactionList.forEach(transaction=>{
    if(transaction.budgetTransactionType==="EXPENSE"){
      balance -= transaction.amount;
      expense += transaction.amount;
      expenseByCat[transaction.budgetCategoryId] = (expenseByCat[transaction.budgetCategoryId]||0)+transaction.amount;
      expenseByUser[transaction.relatedAccountId] = (expenseByUser[transaction.relatedAccountId]||0)+transaction.amount;
    }else{
      balance += transaction.amount;
      revenue += transaction.amount;
    }
  })

  let
    expenseByCatEntries = Object.entries(expenseByCat),
    topPayerEntries = Object.entries(expenseByUser),
    topCategory = expenseByCatEntries.length===0?[]as any:expenseByCatEntries.reduce((previousEntry, currentEntry)=>{
      return previousEntry[1]<currentEntry[1]?currentEntry:previousEntry}
      ,expenseByCatEntries[0]),
    topPayer = topPayerEntries.length===0?[]as any:topPayerEntries.reduce((previousEntry, currentEntry)=>{
      return previousEntry[1]<currentEntry[1]?currentEntry:previousEntry}
      ,topPayerEntries[0]);

  function roundIfOk(amount: number): string | null {
    if(Math.round(amount)===amount){
      return budget.formatAmountRound(amount)
    }else{
      return budget.formatAmount(amount)
    }
  }
  var topCategoryCached = FwDatas.get(topCategory[0])
  return {
    balance : balance,
    balanceString : roundIfOk(balance),
    revenue : revenue,
    revenueString : roundIfOk(revenue),
    expense : expense,
    expenseString : roundIfOk(expense),

    topCategoryTitle : topCategoryCached?topCategoryCached.name:null, //should never happen!!
    topCategory : topCategory[1],
    topCategoryString : roundIfOk(topCategory[1]),
    topPayerTitle : FwDatas.getProfile(topPayer[0]).name,
    topPayer : topPayer[1],
    topPayerString : roundIfOk(topPayer[1])
  }
}

/*
var lastStart;
for(var date=new Date('2020-1-1');date<new Date('2040-12-31');){
  var range = DateUtils.getWeekRange(date,7,2);
  date = range.next;
  if(lastStart!==undefined){
    var lastStartIncremented = new Date(lastStart)
    lastStartIncremented.setDate(lastStart.getDate()+14);
    console.log(range.start.getTime()===lastStartIncremented.getTime());
  }
  lastStart = range.start;
}*/
